<template>
  <BaseModal
    class="modal-contact"
    modal-id="modalContact"
    :show-modal="props.showModal"
  >
    <template #heading>Send confirmation</template>
    <template #content>
      <span><!-- fix for mysterious non-showing content locally --></span>
      <ContactPhone :is-modal="Boolean(true)" @doPhone="setPhone" @doResetVerify="resetSendNow" />
      <p hidden ref="invalidPhoneMsg" class="app-invalid-msg bad-phone">Please enter a valid phone number.</p>
      <ContactEmail :is-modal="Boolean(true)" @doEmail="setEmail" @doResetVerify="resetSendNow" />
      <p hidden ref="noContactInfo" class="app-invalid-msg msg-offset-1">
        Email is required.
      </p>
      <p></p>
    </template>
    <template #footing>
      <div class="footer-offset">
        <ModalDisclaimer @doDisclaimer="adjustModalHeight" />
        <BaseButtonGroup>
          <button type="button" @click="closeModal">Cancel</button>
          <CustomButton type="submit" @click="notifyMe">Send</CustomButton>
        </BaseButtonGroup>
      </div>
    </template>
  </BaseModal>
</template>


<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import BaseModal from '@/components/ui/BaseModal';
import ContactPhone from '@/components/contact/ContactPhone';
import ContactEmail from '@/components/contact/ContactEmail';
import ModalDisclaimer from '@/components/confirmation/ModalDisclaimer';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import CustomButton from '../CustomButton.vue';
import * as COMMONS from '../../store/_commons';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter'; 

const props = defineProps({
  showModal: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['doClose', 'doNotificationMessage']);

const store = useStore();

const myPhone = ref('');
const myEmail = ref('');
const isPhoneValidated = ref(true);
const isEmailValidated = ref(true);
const sendNow = ref(false);

const noContactInfo = ref(null);
const invalidPhoneMsg = ref(null);

// Use the phone composable
const { formatPhoneForRims } = usePhoneFormatter();

// Computed
const finalizationCart = computed(() => store.state.guestLogin.finalizationCart);

// --- Methods ---
const adjustModalHeight = (collapsed) => {
  const modalContent = document.querySelector('.app-modal-content');
  if (!modalContent) return;

  const overflowSelector = '__overflow';
  if (collapsed) {
    modalContent.classList.remove(overflowSelector);
  } 
  // 400px from media query max-height
  else if (screen.height > 400 && modalContent.offsetHeight > (screen.height * 0.9)) {
    modalContent.classList.add(overflowSelector);
  }
};

const closeModal = () => {
  sendNow.value = false;
  // close modal so that network error message from middle tier will be displayed unblocked
  emit('doClose');
};

const hasContactInfo = () => {
  return myEmail.value.length > 0;
};

const handleNotificationResponse = (response) => {
  emit('doNotificationMessage', {
    isConsentProvided: true,
    isEmailSentSuccessfully: response.isEmailSentSuccessfully,
    isSmsSentSuccessfully: response.isSmsSentSuccessfully,
  });

  // close modal - let parent take care of notification status
  closeModal();
};
/**
 * User hits the Send button.
 */
const notifyMe = async () => {
  sendNow.value = true;
  await sendNotification();
};

const resetSendNow = () => {
  sendNow.value = false;
};
/**
 * Request backend to send notification(s) if everything checks out.
 */
const sendNotification = async () => {
  if (!validatePhoneAgain()) {
    sendNow.value = false;
    return;
  }

  // show/hide required field message
  const hasContact = hasContactInfo();
  toggleContactInfoCheck(hasContact);

  // send notification IFF at least one good contact is provided and there are no standing errors
  if (sendNow.value && isEmailValidated.value && isPhoneValidated.value && hasContact) {
    const formattedPhoneNum = formatPhoneForRims(myPhone.value);
    const data = {
      // appointmentNumber: this.resAppointmentNum,  // "required" only for RIMS email/sms notifications (?)
      email: myEmail.value,
      isConsentProvided: true,
      phone: formattedPhoneNum,
      onlineTransactionCartId: finalizationCart.value.finalizeCartResponse.transactionCart.transactionCartId.toString(),
    };
    // user might have overridden their info...
    await updateContactInfo(data);

    // wait for send response...
    // const response = await getSendTransactionConfiramtion(data);
  }

  // prevent data from being submitted without user's explicit choice
  resetSendNow();
};

/**
 * Depend on ContactEmail myEmail watcher.
 */
const setEmail = (data) => {
  myEmail.value = data.email;
  isEmailValidated.value = data.isValid;
};

/**
 * Depend on ContactEmail myPhone watcher.
 */
const setPhone = (data) => {
  myPhone.value = data.phone;
  isPhoneValidated.value = data.isValid;
};

/**
 * Show or hide required field message based on user input.
 * @param hasContact
 */
const toggleContactInfoCheck = (hasContact) => {
  if (noContactInfo.value) {
    if (hasContact) {
      noContactInfo.value.setAttribute('hidden', 'hidden');
    } else {
      noContactInfo.value.removeAttribute('hidden');
    }
  }
};
/**
 * Refresh session data (phone, email, consent) in case user has provided new info.
 * @param data
 */
const updateContactInfo = async (data) => {
  const paymentSendConfirmationRequest = {
    email: data.email,
    phone: data.phone,
    isConsentProvided: true,
    onlineTransactionCartId: store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart.transactionCartId.toString()
  };
  store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart.email = paymentSendConfirmationRequest.email;
  store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart.phone = paymentSendConfirmationRequest.phone;

  const response = await store.dispatch(COMMONS.GUEST_SENDTRANSACTIONCONFIRMATION, paymentSendConfirmationRequest);
  try {
    await handleNotificationResponse(response);
  } catch (error) {
    console.error(error);
  }
};

const validatePhoneAgain = () => {
  const ac = document.getElementById('userPhoneAC');
  const f3 = document.getElementById('userPhoneF3');
  const l4 = document.getElementById('userPhoneL4');
  const phone2 = `${ac?.value || ''}${f3?.value || ''}${l4?.value || ''}`;

  let okay = true;
  if (phone2.length > 0) {
    if (/^\d+$/.test(phone2)) {
      okay = phone2.length === 10;
    } else {
      okay = false;
    }
    if (sendNow.value && isPhoneValidated.value) {
      toggleInvalidPhoneMsg(!okay);
    }
  }
  return okay;
};

const toggleInvalidPhoneMsg = (forceShow) => {
  if (invalidPhoneMsg.value) {
    if (forceShow) {
      invalidPhoneMsg.value.removeAttribute('hidden');
    } else {
      invalidPhoneMsg.value.setAttribute('hidden', 'hidden');
    }
  }
};
</script>


<style lang="scss" scoped>
.modal-contact {
  form {
    button.verify {   // for verification code (future phase)
      display: inline-block;
      min-width: unset;
    }
    // &::v-deep {      
    :deep() {
      --field-padding: 1em;
      fieldset {
        legend {
          background-color: var(--ri-light-gray);
          padding: var(--field-padding);
          width: 100%;
        }
        .app-contact-inputs {
          padding: var(--field-padding) 0 var(--field-padding) var(--field-padding);
          --box-width: 320px !important;
          --box-width-tiny: calc(var(--box-width) * 0.85) !important;
        }
        .app-input-group .app-contact-info{
          .app-user-email {
            @media only screen and (max-width: 425px) {
              min-width: var(--box-width-tiny);
            }
          }
        } 
      }
    }
    p {
      margin: -0.75em 0 1em;
      &.bad-phone {
        margin: -1.75em 0 1.5em 1em;
      }
    }
    input {
      margin-bottom: 1em;
      ~ p {
        width: unset;
      }
    }
    .msg-offset {
      margin-top: -2em;
    }
  }
  .footer-offset {
    margin-top: -3rem;
  }
  button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}

</style>
